import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import AdminLayout from "./AdminLayout";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { ArrowLeft, Ban, Trash2, ShieldCheck, ShieldOff, UserCheck, KeyRound, Save } from "lucide-react";

function Card({ title, children, testid }) {
    return (
        <div className="bg-surface border border-ink/10 rounded-2xl p-5" data-testid={testid}>
            <div className="sp-overline mb-3">{title}</div>
            {children}
        </div>
    );
}

export default function AdminUserDetail() {
    const { user_id } = useParams();
    const navigate = useNavigate();
    const [d, setD] = useState(null);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", preferred_airport: "", is_admin: false });
    const [pwd, setPwd] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [error, setError] = useState("");

    const refresh = () => {
        api.get(`/admin/users/${user_id}`)
            .then((r) => {
                setD(r.data);
                setForm({
                    name: r.data.user.name || "",
                    email: r.data.user.email || "",
                    preferred_airport: r.data.user.preferred_airport || "",
                    is_admin: !!r.data.user.is_admin,
                });
            })
            .catch((e) => setError(e.response?.data?.detail || e.message));
    };
    useEffect(refresh, [user_id]);

    if (error) return <AdminLayout title="User"><div className="text-sm text-red-500">{error}</div></AdminLayout>;
    if (!d) return <AdminLayout title="User"><div className="text-ink-muted">Loading…</div></AdminLayout>;

    const u = d.user;
    const full = d.full;
    const chart = full.natal_chart || {};

    const runAction = async (kind, body) => {
        try {
            await api.post(`/admin/users/${user_id}/${kind}`, body);
            setConfirm(null);
            setShowPwd(false);
            setPwd("");
            refresh();
        } catch (e) { setError(e.response?.data?.detail || e.message); }
    };

    const saveEdits = async () => {
        try {
            await api.patch(`/admin/users/${user_id}`, form);
            setEditing(false);
            refresh();
        } catch (e) { setError(e.response?.data?.detail || e.message); }
    };


    return (
        <AdminLayout>
            <Link to="/admin/users" className="inline-flex items-center gap-2 text-xs text-ink-muted hover:text-ink mb-4" data-testid="admin-user-back">
                <ArrowLeft strokeWidth={1.5} className="w-3 h-3" /> Back to users
            </Link>

            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="font-serif text-3xl tracking-tight" data-testid="admin-user-name">{u.name || "Unnamed"}</h1>
                    <div className="text-ink-muted text-sm mt-1" data-testid="admin-user-email">{u.email}</div>
                    <div className="text-xs text-ink-muted mt-1 font-mono">{u.user_id}</div>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="admin-user-actions">
                    {!editing && <button className="sp-btn-ghost text-xs" onClick={() => setEditing(true)} data-testid="action-edit">Edit</button>}
                    {u.status !== "blocked" ? (
                        <button className="sp-btn-ghost text-xs" onClick={() => setConfirm({ kind: "block", title: "Block this user?", body: `${u.email} will be unable to sign in.`, action: "Block" })} data-testid="action-block">
                            <Ban strokeWidth={1.5} className="w-3.5 h-3.5" /> Block
                        </button>
                    ) : (
                        <button className="sp-btn-ghost text-xs" onClick={() => runAction("unblock")} data-testid="action-unblock">
                            <ShieldCheck strokeWidth={1.5} className="w-3.5 h-3.5" /> Unblock
                        </button>
                    )}
                    {u.status !== "deactivated" ? (
                        <button className="sp-btn-ghost text-xs" onClick={() => setConfirm({ kind: "deactivate", title: "Deactivate this user?", body: "They will lose access until reactivated.", action: "Deactivate" })} data-testid="action-deactivate">
                            <ShieldOff strokeWidth={1.5} className="w-3.5 h-3.5" /> Deactivate
                        </button>
                    ) : (
                        <button className="sp-btn-ghost text-xs" onClick={() => runAction("reactivate")} data-testid="action-reactivate">
                            <UserCheck strokeWidth={1.5} className="w-3.5 h-3.5" /> Reactivate
                        </button>
                    )}
                    <button className="sp-btn-ghost text-xs" onClick={() => setShowPwd(true)} data-testid="action-reset-pwd">
                        <KeyRound strokeWidth={1.5} className="w-3.5 h-3.5" /> Reset Password
                    </button>
                    <button className="sp-btn-ghost text-xs text-red-600" onClick={() => setConfirm({ kind: "delete", title: "Delete this user permanently?", body: "Soft-delete: deleted_at timestamp is added. The record is NOT removed from MongoDB.", action: "Delete User" })} data-testid="action-delete">
                        <Trash2 strokeWidth={1.5} className="w-3.5 h-3.5" /> Delete
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <Card title="Profile" testid="card-profile">
                    {editing ? (
                        <div className="space-y-3 text-sm">
                            <div><label className="text-xs text-ink-muted">Name</label><input className="sp-input w-full mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" /></div>
                            <div><label className="text-xs text-ink-muted">Email</label><input className="sp-input w-full mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-email" /></div>
                            <div><label className="text-xs text-ink-muted">Preferred Airport</label><input className="sp-input w-full mt-1" value={form.preferred_airport} onChange={(e) => setForm({ ...form, preferred_airport: e.target.value })} data-testid="edit-airport" /></div>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} data-testid="edit-is-admin" />
                                Admin privileges
                            </label>
                            <div className="flex gap-2 pt-2">
                                <button className="sp-btn-primary text-xs" onClick={saveEdits} data-testid="edit-save"><Save strokeWidth={1.5} className="w-3.5 h-3.5 inline mr-1" />Save</button>
                                <button className="sp-btn-ghost text-xs" onClick={() => setEditing(false)} data-testid="edit-cancel">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <dl className="text-sm space-y-1.5">
                            <div className="flex justify-between"><dt className="text-ink-muted">Auth</dt><dd>{full.auth_method}</dd></div>
                            <div className="flex justify-between"><dt className="text-ink-muted">Created</dt><dd>{(u.created_at || "").slice(0, 19).replace("T", " ")}</dd></div>
                            <div className="flex justify-between"><dt className="text-ink-muted">Last Login</dt><dd>{(u.last_login || "").slice(0, 19).replace("T", " ") || "—"}</dd></div>
                            <div className="flex justify-between"><dt className="text-ink-muted">Status</dt><dd>{u.status}</dd></div>
                            <div className="flex justify-between"><dt className="text-ink-muted">Admin</dt><dd>{u.is_admin ? "Yes" : "No"}</dd></div>
                            <div className="flex justify-between"><dt className="text-ink-muted">Preferred Airport</dt><dd>{u.preferred_airport || "—"}</dd></div>
                        </dl>
                    )}
                </Card>

                <Card title="Birth Information" testid="card-birth">
                    {full.birth ? (
                        <dl className="text-sm space-y-1.5">
                            <div className="flex justify-between"><dt className="text-ink-muted">Date</dt><dd>{full.birth.date_of_birth}</dd></div>
                            <div className="flex justify-between"><dt className="text-ink-muted">Time</dt><dd>{full.birth.time_of_birth}</dd></div>
                            <div className="flex justify-between"><dt className="text-ink-muted">City</dt><dd>{full.birth.birth_city}</dd></div>
                            <div className="flex justify-between"><dt className="text-ink-muted">Country</dt><dd>{full.birth.birth_country}</dd></div>
                            {full.birth.birth_lat && <div className="flex justify-between"><dt className="text-ink-muted">Coords</dt><dd className="font-mono text-xs">{full.birth.birth_lat?.toFixed(3)}, {full.birth.birth_lng?.toFixed(3)}</dd></div>}
                        </dl>
                    ) : <div className="text-xs text-ink-muted">No birth data on file.</div>}
                </Card>

                <Card title="Natal Chart" testid="card-natal">
                    <dl className="text-sm space-y-1.5">
                        <div className="flex justify-between"><dt className="text-ink-muted">Sun</dt><dd>☉ {chart.sun || "—"}</dd></div>
                        <div className="flex justify-between"><dt className="text-ink-muted">Moon</dt><dd>☽ {chart.moon || "—"}</dd></div>
                        <div className="flex justify-between"><dt className="text-ink-muted">Rising</dt><dd>↑ {chart.rising || "—"}</dd></div>
                        <div className="flex justify-between"><dt className="text-ink-muted">Saved Trips</dt><dd>{d.counts.saved}</dd></div>
                        <div className="flex justify-between"><dt className="text-ink-muted">Recs Viewed</dt><dd>{d.counts.recommendations}</dd></div>
                        <div className="flex justify-between"><dt className="text-ink-muted">Flight Searches</dt><dd>{d.counts.flights}</dd></div>
                        <div className="flex justify-between"><dt className="text-ink-muted">Login Events</dt><dd>{d.counts.logins}</dd></div>
                    </dl>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card title="Saved Destinations" testid="card-saved">
                    {d.saved_trips.length === 0 ? <div className="text-xs text-ink-muted">None saved yet.</div> : (
                        <ul className="text-sm space-y-2 max-h-80 overflow-auto">
                            {d.saved_trips.map((t) => (
                                <li key={t.trip_id} className="flex items-center justify-between gap-3">
                                    <div>
                                        <div>{t.snapshot?.city}, {t.snapshot?.country}</div>
                                        <div className="text-xs text-ink-muted">{(t.created_at || "").slice(0, 10)} · {t.season}</div>
                                    </div>
                                    <span className="text-xs text-ink-muted">{t.visited ? "Visited" : "Planned"}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
                <Card title="Recommendation History" testid="card-rec-history">
                    {d.recommendation_history.length === 0 ? <div className="text-xs text-ink-muted">No recommendations viewed.</div> : (
                        <ul className="text-sm space-y-1.5 max-h-80 overflow-auto">
                            {d.recommendation_history.map((e, i) => (
                                <li key={i} className="flex items-center justify-between">
                                    <span>{e.destination_id || "—"}</span>
                                    <span className="text-xs text-ink-muted">{(e.created_at || "").slice(0, 19).replace("T", " ")}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
                <Card title="Flight Search History" testid="card-flight-history">
                    {d.flight_history.length === 0 ? <div className="text-xs text-ink-muted">No flight searches yet.</div> : (
                        <ul className="text-sm space-y-1.5 max-h-80 overflow-auto">
                            {d.flight_history.map((e, i) => (
                                <li key={i} className="flex items-center justify-between">
                                    <span>{e.destination_id || "—"}</span>
                                    <span className="text-xs text-ink-muted">{(e.created_at || "").slice(0, 19).replace("T", " ")}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
                <Card title="Login History" testid="card-login-history">
                    {d.login_history.length === 0 ? <div className="text-xs text-ink-muted">No login events.</div> : (
                        <ul className="text-sm space-y-1.5 max-h-80 overflow-auto">
                            {d.login_history.map((e, i) => (
                                <li key={i}><span className="text-xs text-ink-muted">{(e.created_at || "").slice(0, 19).replace("T", " ")}</span></li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>

            {/* Confirm dialog */}
            <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
                <AlertDialogContent data-testid="user-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
                        <AlertDialogDescription>{confirm?.body}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="user-confirm-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => runAction(confirm.kind)} data-testid="user-confirm-action">{confirm?.action}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Reset password dialog */}
            <AlertDialog open={showPwd} onOpenChange={setShowPwd}>
                <AlertDialogContent data-testid="reset-password-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reset password</AlertDialogTitle>
                        <AlertDialogDescription>Set a new password for {u.email}. They will need to use this on their next login.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <input type="text" className="sp-input w-full" placeholder="New password (min 8 chars)" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-password-input" />
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="reset-password-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => runAction("reset-password", { new_password: pwd })} disabled={pwd.length < 8} data-testid="reset-password-submit">Reset</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AdminLayout>
    );
}
